import {
  type Chat,
  type ChatMessage,
  type EmailAttachment,
  isEmailAddress,
  LinkError,
  MAX_EMAIL_ATTACHMENT_BYTES,
  MAX_EMAIL_ATTACHMENTS,
  parseAddresses,
} from "@rhema/client";
import { Button, cn, FileTextIcon, focusRing, LockIcon, Sheet, TextField } from "@rhema/ui";
import { useId, useMemo, useState } from "react";
import { formatOf, plainOf } from "../conversation/rich";
import { clockTime } from "../data/view";
import { formatBytes } from "../media/prepare";
import { useClient } from "../session/session";
import { LinkForm } from "./LinkForm";
import { useLinkStatus, useLinks } from "./useLinks";

type Media = Extract<NonNullable<ChatMessage["content"]>, { type: "media" }>;

function nameOf(chat: Chat, id: string | null, meId?: string): string {
  if (id && id === meId) return "Me";
  return chat.members.find((m) => m.user.id === id)?.user.display_name ?? "Someone";
}

function lineOf(m: ChatMessage): string {
  const c = m.content;
  if (!c) return "";
  // An email is plain text, so formatting marks go and link addresses stay.
  if (c.type === "text") return plainOf(m.edited ?? c.body, formatOf(m), true);
  if (c.type === "media") return plainOf(m.edited ?? c.caption ?? "", formatOf(m), true);
  if (c.type === "location") return `Location: ${c.lat}, ${c.lng}`;
  if (c.type === "contact") return `Contact: ${c.name}`;
  if (c.type === "poll") return `Poll: ${c.question}`;
  return "";
}

/** The email body for the chosen messages. One message is just its text. */
export function emailBody(chat: Chat, messages: ChatMessage[], meId?: string): string {
  if (messages.length === 1) return lineOf(messages[0] as ChatMessage);
  return messages
    .map((m) => {
      const text = lineOf(m) || (m.content?.type === "media" ? "(attached)" : "");
      return `${nameOf(chat, m.senderId, meId)}, ${clockTime(m.createdAt)}: ${text}`;
    })
    .join("\n");
}

function mediaOf(messages: ChatMessage[]): Media[] {
  return messages.map((m) => m.content).filter((c): c is Media => c?.type === "media");
}

function fileName(c: Media, i: number): string {
  if (c.media.name) return c.media.name;
  const ext = c.media.mime.split("/")[1]?.replace("jpeg", "jpg") ?? "bin";
  return `${c.kind === "video" ? "Video" : c.kind === "photo" ? "Photo" : "File"} ${i + 1}.${ext}`;
}

/**
 * Turns messages into a Malakh draft. Saving a draft never sends it. Send is its own button and
 * goes through Malakh. Either way the email leaves Rhema's encryption.
 */
export function EmailComposer({
  open,
  chat,
  chatTitle,
  messages,
  meId,
  onClose,
  onDone,
}: {
  open: boolean;
  chat: Chat;
  chatTitle: string;
  messages: ChatMessage[];
  meId?: string;
  onClose: () => void;
  /** Called with a line to show once the draft is saved or the email is sent. */
  onDone: (note: string) => void;
}) {
  const client = useClient();
  const all = useLinks();
  // The server checks the draft against the chat the messages come from.
  const links = useMemo(() => all.inChat(chat.id), [all, chat.id]);
  const { status, enabled } = useLinkStatus("malakh");
  const bodyId = useId();
  const media = useMemo(() => mediaOf(messages), [messages]);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`Messages from ${chatTitle}`);
  const [body, setBody] = useState(() => emailBody(chat, messages, meId));
  const [busy, setBusy] = useState<"draft" | "send" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The draft saved for exactly these fields, so Send does not save it twice.
  const [saved, setSaved] = useState<{ key: string; emailId: string } | null>(null);

  const total = media.reduce((n, c) => n + c.media.size, 0);
  const tooMany = media.length > MAX_EMAIL_ATTACHMENTS;
  const tooBig = total > MAX_EMAIL_ATTACHMENT_BYTES;
  const addresses = parseAddresses(to);
  const key = JSON.stringify([addresses, subject, body]);

  const attachments = async (): Promise<EmailAttachment[]> => {
    const out: EmailAttachment[] = [];
    for (const [i, c] of media.entries()) {
      // Decrypted here, on this device, just before it goes to Malakh.
      const blob = await client.media.download(c.media);
      out.push({ blob, name: fileName(c, i), type: c.media.mime });
    }
    return out;
  };

  const check = (needTo: boolean): string | null => {
    if (needTo && !addresses.length) return "Add at least one address.";
    const bad = addresses.find((a) => !isEmailAddress(a));
    if (bad) return `${bad} is not an email address.`;
    if (tooMany) return `An email can carry up to ${MAX_EMAIL_ATTACHMENTS} files.`;
    if (tooBig) return "Attachments can be up to 25 MB in all.";
    return null;
  };

  const draft = async (): Promise<string> => {
    if (saved?.key === key) return saved.emailId;
    const { emailId } = await links.malakh.createDraft({
      to: addresses,
      subject,
      body,
      attachments: await attachments(),
    });
    setSaved({ key, emailId });
    return emailId;
  };

  const run = async (kind: "draft" | "send") => {
    const problem = check(kind === "send");
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(kind);
    setError(null);
    try {
      const emailId = await draft();
      if (kind === "send") {
        await links.malakh.send(emailId, addresses);
        onDone("Email sent with Malakh.");
      } else {
        onDone("Draft saved in Malakh.");
      }
    } catch (err) {
      setError(
        err instanceof LinkError && err.code === "not_linked"
          ? "Link Malakh in Settings first."
          : (err as Error).message || "Malakh did not take the email.",
      );
    } finally {
      setBusy(null);
    }
  };

  const linked = Boolean(status?.linked);

  return (
    <Sheet open={open} onClose={onClose} fixed={false} title="Email with Malakh" className="px-5 pb-7">
      <div className="mt-3 flex items-start gap-2.5 rounded-field bg-surface-header px-3.5 py-3">
        <LockIcon size={17} strokeWidth={2.2} className="mt-0.5 flex-none text-scarlet" />
        <p className="m-0 text-sm leading-normal text-ink">
          This email leaves Rhema's end-to-end encryption. Malakh and the people you send it to can read it.
        </p>
      </div>

      {enabled === false && (
        <p className="m-0 mt-3 text-[15px] text-ink-secondary">
          Connected apps are turned off on this server.
        </p>
      )}
      {enabled !== false && !status && (
        <p className="m-0 mt-3 text-sm text-ink-secondary" role="status">
          Checking Malakh.
        </p>
      )}
      {status && !status.available && (
        <p className="m-0 mt-3 text-[15px] text-ink-secondary">Malakh cannot be reached right now.</p>
      )}
      {status?.available && !linked && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="m-0 text-[15px] text-ink">Link your Malakh account to save emails there.</p>
          <LinkForm product="malakh" onCancel={onClose} />
        </div>
      )}

      {status?.available && linked && (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void run("draft");
          }}
        >
          <TextField
            label="To"
            type="text"
            inputMode="email"
            autoComplete="email"
            hint="Put a comma between addresses."
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <div className="flex flex-col gap-1.5">
            <label htmlFor={bodyId} className="text-[13px] font-semibold text-ink">
              Message
            </label>
            <textarea
              id={bodyId}
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={cn(
                "rounded-field border border-line bg-surface px-4 py-3 font-sans text-[15px] text-ink focus:border-blue",
                focusRing,
              )}
            />
          </div>
          {media.length > 0 && (
            <div>
              <p className="m-0 text-[13px] font-semibold">
                {media.length === 1 ? "1 attachment" : `${media.length} attachments`}, {formatBytes(total)}
              </p>
              <ul aria-label="Attachments" className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
                {media.map((c, i) => (
                  <li key={c.media.key || i} className="flex items-center gap-2 text-sm text-ink-secondary">
                    <FileTextIcon size={15} className="flex-none" />
                    <span className="truncate">{fileName(c, i)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && (
            <p role="alert" className="m-0 text-sm font-semibold text-scarlet">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button type="submit" variant="tonal" size="lg" disabled={busy !== null}>
              {busy === "draft" ? "Saving the draft" : "Save as draft"}
            </Button>
            <Button size="lg" disabled={busy !== null} onClick={() => void run("send")}>
              {busy === "send" ? "Sending" : "Send now"}
            </Button>
          </div>
          <p className="m-0 text-xs text-ink-meta">A draft stays in Malakh until you send it.</p>
        </form>
      )}
    </Sheet>
  );
}
